import { useCallback } from 'react';
import { useForm } from '@mantine/form';
import { Stack, TextInput, FileInput, Text } from '@mantine/core';
import { useNavigateWithSearch } from '@/hooks/useNavigateWithSearch';
import { useUploadCsvToCreateListMutation } from '@/features/customListSlice';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import TagManager from 'react-gtm-module';
import { GoogleEvents } from '@/features/googleEvents';
import { LISTS_BASE_PATH } from '@/app/constants';
import Modal from '@/components/Modals/Modal';
import { useRunListAction } from './customListSelectorUtils';
import { Icon } from '@/components';
import { Upload } from 'react-feather';

interface CsvUploadForm {
  name: string;
  csvFile: File | null;
  level2Header: string;
  level3Header: string;
}

interface CreateProjectFromCsvModalProps {
  opened: boolean;
  onClose: () => void;
}

export function CreateProjectFromCsvModal({
  opened,
  onClose,
}: CreateProjectFromCsvModalProps) {
  const navigateWithSearch = useNavigateWithSearch();
  const intl = useClientTerminology();
  const [uploadCsv, { isLoading }] = useUploadCsvToCreateListMutation();
  const runListAction = useRunListAction(onClose, {
    successTitle: intl.formatMessage({ id: 'action.success' }),
    successMessage: intl.formatMessage({ id: 'customList.createListSuccess' }),
  });

  const form = useForm<CsvUploadForm>({
    initialValues: {
      name: '',
      csvFile: null,
      level2Header: 'level2',
      level3Header: 'level3',
    },
    validate: {
      name: (value) =>
        value.trim().length === 0
          ? intl.formatMessage({
              id: 'customList.nameRequired',
              defaultMessage: 'List name is required',
            })
          : null,
      csvFile: (value) =>
        !value
          ? intl.formatMessage({ id: 'customList.csvFileRequired' })
          : null,
      level2Header: (value) =>
        value.trim().length === 0
          ? intl.formatMessage({ id: 'customList.level2HeaderRequired' })
          : null,
      level3Header: (value) =>
        value.trim().length === 0
          ? intl.formatMessage({ id: 'customList.level3HeaderRequired' })
          : null,
    },
  });

  const handleSubmit = useCallback(
    async (values: CsvUploadForm) => {
      if (!values.csvFile) return;

      TagManager.dataLayer({
        dataLayer: {
          event: GoogleEvents.projects.creation.newProjectSaved,
          params: {
            projectName: values.name,
            uploadMethod: 'csv',
          },
        },
      });

      const result = await runListAction(
        uploadCsv({
          listName: values.name,
          csvFile: values.csvFile,
          level2Header: values.level2Header,
          level3Header: values.level3Header,
        }).unwrap
      );

      if (result) {
        navigateWithSearch(`${LISTS_BASE_PATH}/${result.summary.id}`);
      }
    },
    [uploadCsv, runListAction, navigateWithSearch]
  );

  const handleFormSubmit = form.onSubmit(handleSubmit);

  const handleFormCancel = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleFormCancel}
      title={intl.formatMessage({ id: 'customList.createProjectFromCsv' })}
      primaryButtonTestId="submit-csv-upload-button"
      secondaryButtonTestId="cancel-csv-upload-button"
      isLoading={isLoading}
      secondaryLabel={intl.formatMessage({
        id: 'action.cancel',
      })}
      primaryLabel={intl.formatMessage({ id: 'customList.uploadCsv' })}
      onSecondaryAction={handleFormCancel}
      formToSubmit="csv-upload-form"
      testId="create-project-from-csv-modal"
    >
      <form id="csv-upload-form" onSubmit={handleFormSubmit} autoComplete="off">
        <Stack gap="md">
          <TextInput
            label={intl.formatMessage({
              id: 'customList.name',
              defaultMessage: 'List name',
            })}
            placeholder={intl.formatMessage({
              id: 'customList.namePlaceholder',
              defaultMessage: 'List name',
            })}
            {...form.getInputProps('name')}
            required
            data-autofocus
            data-testid="csv-upload-name-input"
          />

          <FileInput
            label={intl.formatMessage({ id: 'customList.csvFileLabel' })}
            placeholder={intl.formatMessage({ id: 'customList.selectCsvFile' })}
            accept=".csv"
            leftSection={<Icon Component={Upload} size={16} />}
            {...form.getInputProps('csvFile')}
            required
            data-testid="csv-upload-file-input"
          />

          <Text size="sm" c="dimmed">
            {intl.formatMessage({ id: 'customList.csvUploadDescription' })}
          </Text>

          <TextInput
            label={intl.formatMessage({ id: 'customList.level2ColumnName' })}
            placeholder={intl.formatMessage({
              id: 'customList.level2Placeholder',
            })}
            {...form.getInputProps('level2Header')}
            required
            data-testid="csv-upload-level2-input"
          />

          <TextInput
            label={intl.formatMessage({ id: 'customList.level3ColumnName' })}
            placeholder={intl.formatMessage({
              id: 'customList.level3Placeholder',
            })}
            {...form.getInputProps('level3Header')}
            required
            data-testid="csv-upload-level3-input"
          />
        </Stack>
      </form>
    </Modal>
  );
}
